import {async} from 'lib/utilities';

interface AnyObject {
    [name: string]: any;
    [name: number]: any;
}

type Prop = string | number;

function isKey(key: any): key is Prop {
    const type = typeof key;

    return type === 'number' || type === 'string' || type === 'symbol';
}

export default function watch<O extends AnyObject, T>(object: O, get: ((obj: O) => T | T[]), watcher: (val: T, key: keyof O) => any) {
    const keys = resolveTargetKeys(object, get);

    return watch.props(object, keys, watcher);
}

watch.props = function<O extends AnyObject, K extends keyof O>(object: O, props: K[] | K, watcher: (newVal: O[K], oldVal: O[K], key: keyof O) => any) {
    const flags = {isCallingWatcher: false};
    const keys = Array.isArray(props) ? props : [props];
    const watchers = keys.map(key => defineKeyWatcher(object, key, watcher, flags));

    return {
        remove: () => watchers.forEach(watcher => watcher.remove())
    };
};

watch.get = function<O extends AnyObject, K extends keyof O>(object: O, props: K[] | K): (obj: O) => O[K] {
    return (obj: O) => {
        if (Array.isArray(props)) {
            return props.map(key => obj[key])[0];
        } else {
            return obj[props];
        }
    }
};

watch.all = function<O extends AnyObject, T extends O[keyof O]>(object: O, watcher: (newVal: T, oldVal: T, key: keyof O) => any) {
    return watch.props(object, Object.keys(object), watcher);
};

function defineKeyWatcher<O extends AnyObject, T>(object: O, prop: keyof O, watcher: (newVal: T, oldVal: T, key: keyof O) => any, flags: {isCallingWatcher: boolean}) {
    const originalDescriptor = Object.getOwnPropertyDescriptor(object, prop);

    if (! originalDescriptor.configurable) {
        throw new Error(`Cannot watch non-configurable property ${prop}. Object logged above.`)
    }

    let value: T = object[prop];

    const getValue = () => {
        if (originalDescriptor.get) {
            return originalDescriptor.get.call(object);
        } else {
            return value;
        }
    };

    const setValue = (newValue: T) => {
        value = newValue;

        if (originalDescriptor.set) {
            originalDescriptor.set.call(object, newValue);
        }
    };

    Object.defineProperty(object, prop, {
        get() {
            return getValue();
        },
        set(newValue: typeof value) {
            // Only proceed with firing the watcher if the value actually changed
            if (newValue === value) {
                return;
            }

            let oldValue = value;

            setValue(newValue);

            if (flags.isCallingWatcher) {
                throw new Error(`Cannot set watched property ${prop} from inside the watcher function.`);
            }

            try {
                flags.isCallingWatcher = true;
                watcher(newValue as any as T, oldValue, prop);
            } catch (error) {
                console.error(`Error caught in watcher, triggered by property '${prop}'`);
                async((err) => {throw err}, error);
            } finally {
                flags.isCallingWatcher = false;
            }
        },
        configurable: true,
        enumerable: originalDescriptor.enumerable
    });

    return {
        remove: () => Object.defineProperty(object, prop, originalDescriptor)
    };
}

function resolveTargetKeys<O extends AnyObject, T>(object: O, keys: keyof O | (keyof O)[] | ((obj: O) => T)) {
    if (typeof keys === 'function') {
        const resolvedKeys: (keyof O)[] = [];

        const proxy = Proxy.revocable(object, {
            get(object, prop: string) {
                resolvedKeys.push(prop);

                return object[prop];
            },
            set(object, prop: string, value) {
                resolvedKeys.push(prop);

                object[prop as keyof O] = value;

                return true;
            }
        });

        keys(proxy.proxy);

        proxy.revoke();

        return resolvedKeys;
    } else if (isKey(keys)) {
        return [keys as keyof O];
    } else {
        return keys as (keyof O)[];
    }
}
